
import { useState } from 'react';
import { Download, File, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';

interface FileDownloadProps {
  name: string;
  path: string;
  size?: string;
  type?: string;
  className?: string;
}

const FileDownload = ({ name, path, size, type, className }: FileDownloadProps) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);

  const handleDownload = () => {
    setIsDownloading(true);
    
    // Simulate download delay (remove in production)
    setTimeout(() => {
      setIsDownloading(false);
      setIsDownloaded(true);
      
      toast({
        title: "Download started",
        description: `${name} is being downloaded.`,
      });
      
      // Reset the downloaded state after a while
      setTimeout(() => setIsDownloaded(false), 3000);
    }, 800);
  };

  const getFileIcon = () => {
    if (isDownloading) return <Download className="h-4 w-4 animate-pulse" />;
    if (isDownloaded) return <Check className="h-4 w-4 text-green-500" />;
    return <File className="h-4 w-4" />;
  };

  return (
    <div className={cn(
      "flex items-center justify-between p-4 border rounded-md bg-background transition-all",
      "hover:border-primary/50 hover:shadow-soft",
      className
    )}>
      <div className="flex items-center space-x-3">
        <div className="p-2 rounded-md bg-muted">
          {getFileIcon()}
        </div>
        <div>
          <p className="font-medium">{name}</p>
          <p className="text-xs text-muted-foreground">
            {type}{size && ` • ${size}`}
          </p>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={handleDownload}
        disabled={isDownloading}
        className="gap-1"
      >
        <Download className="h-4 w-4" />
        <span>Download</span>
      </Button>
    </div>
  );
};

export default FileDownload;
